import { BasicInfo } from './basic-info';
import { ToneInfo } from './tone-info';
import { Stack, Banner, Text, FormattedDateTime } from '@customerio/pluma-components/react';
import { Form, withForm } from '@/components/form';
import { type ChangeInfo, useAIBusinessProfileForm } from '../hooks';
import { formOpts } from '../helpers/form';
import type { AIContext, AIContextOverrides } from '../types';
import { KeyLinksInfo } from './key-links-info';
import { PlatformsInfo } from './platforms-info';
import { useNavigationGuard } from '@/services/navigation-guard';
import { useEffect, useRef } from 'react';
import type { FormData } from '../helpers/form';
import { isEmptyString } from '@/utils/string';

const MIN_VALID_DATE = new Date('2020-01-01');

const cleanDomain = (domain: string): string => {
  return domain.replace(/^https?:\/\//, '');
};

const AIBusinessProfileForm = withForm({
  ...formOpts,
  props: {
    canEdit: true,
  },
  render: function AIBusinessProfileForm({ form, canEdit }) {
    return (
      <Stack gap="300">
        <BasicInfo form={form} canEdit={canEdit} />
        <ToneInfo form={form} canEdit={canEdit} />
        <KeyLinksInfo form={form} canEdit={canEdit} />
        <PlatformsInfo form={form} canEdit={canEdit} />
      </Stack>
    );
  },
});

interface AIBusinessProfileWrapperProps {
  aiContext?: AIContext;
  overrides?: AIContextOverrides;
  formId: string;
  canEdit?: boolean;
  onSave?: (changeInfo: ChangeInfo & { editSessionMs: number }) => void;
  onDiscard?: (unsavedFieldsChangedCount: number) => void;
  onEditIntent?: (fieldName: string) => void;
  children: (formState: { isDirty: boolean; isValid: boolean; formContent: React.ReactNode }) => React.ReactNode;
}

/**
 * Wrapper component that handles form state initialization and provides
 * render props pattern for accessing form state.
 */
export const AIBusinessProfileWrapper = ({
  aiContext,
  overrides,
  formId,
  canEdit = true,
  onSave,
  onDiscard,
  onEditIntent,
  children,
}: AIBusinessProfileWrapperProps) => {
  const formState = useAIBusinessProfileForm(aiContext, overrides, onSave);
  const showGenerationInfo =
    aiContext != null && !isEmptyString(aiContext.updated_at) && new Date(aiContext.updated_at) > MIN_VALID_DATE;
  const focusedFieldsRef = useRef<Set<string>>(new Set());

  // Track field focus for edit intent analytics
  useEffect(() => {
    if (!canEdit || onEditIntent == null) return;

    const formElement = document.getElementById(formId);
    if (formElement == null) return;

    const getFieldLabel = (field: HTMLElement): string | null => {
      const ariaLabel = field.getAttribute('aria-label');
      if (ariaLabel != null && !isEmptyString(ariaLabel)) return ariaLabel;

      const fieldId = field.getAttribute('id');
      if (fieldId != null && !isEmptyString(fieldId)) {
        const label = document.querySelector(`label[for="${fieldId}"]`);
        const labelText = label?.textContent;
        if (labelText != null && !isEmptyString(labelText)) return labelText.trim();
      }

      const parentLabel = field.closest('label');
      const parentLabelText = parentLabel?.textContent;
      if (parentLabelText != null && !isEmptyString(parentLabelText)) {
        const fieldText = field.textContent ?? '';
        const labelText = parentLabelText.replace(fieldText, '').trim();
        if (!isEmptyString(labelText)) return labelText;
      }

      return field.getAttribute('name') ?? field.getAttribute('id');
    };

    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target as HTMLElement;
      const field = target.closest('input, textarea, select, [role="combobox"]') as HTMLElement;
      if (field == null) return;

      const fieldName = getFieldLabel(field);
      if (fieldName == null || focusedFieldsRef.current.has(fieldName)) return;

      focusedFieldsRef.current.add(fieldName);
      formState.startEditSession();
      onEditIntent(fieldName);
    };

    formElement.addEventListener('focusin', handleFocusIn);
    return () => formElement.removeEventListener('focusin', handleFocusIn);
  }, [canEdit, onEditIntent, formId, formState]);

  useNavigationGuard({
    hasUnsavedChanges: canEdit && formState.isDirty,
    onDiscard: () => {
      const fieldKeys = Object.keys(formState.form.state.values) as (keyof FormData)[];
      const changedCount = fieldKeys.filter((key) => {
        const field = formState.form.state.fieldMeta[key];
        return field?.isDirty === true;
      }).length;

      if (onDiscard != null) {
        onDiscard(changedCount);
      }

      formState.form.reset();
    },
    onSave: async () => {
      await formState.form.handleSubmit();
    },
  });

  return (
    <>
      {children({
        isDirty: formState.isDirty,
        isValid: formState.isValid,
        formContent: (
          <Stack gap="300">
            {showGenerationInfo ? (
              <Banner>
                <Text>
                  The information below was automatically generated by analyzing your sending domain{' '}
                  <Text as="span" weight="medium" fontStyle="italic">
                    {cleanDomain(aiContext.domain)}
                  </Text>{' '}
                  on{' '}
                  <Text as="span" weight="medium">
                    <FormattedDateTime date={aiContext.updated_at} />
                  </Text>
                  . Our AI crawlers and enrichment pipeline gathered this data from your website and public sources. You
                  can override any details that seem inaccurate — AI can sometimes hallucinate.
                </Text>
              </Banner>
            ) : null}
            <Form api={formState.form} id={formId}>
              <AIBusinessProfileForm form={formState.form} canEdit={canEdit} />
            </Form>
          </Stack>
        ),
      })}
    </>
  );
};

export { AIBusinessProfileForm };
